import { useState, useEffect } from 'react'
import {
  Box, Container, Paper, Text, Group, Stack, Loader,
  Drawer, Badge, Divider, UnstyledButton, Pagination, Modal, ActionIcon,
} from '@mantine/core'
import { CheckCircle, HelpCircle, XCircle, BookOpen, ExternalLink, Award } from 'lucide-react'
import { vocabularyApi, WordStatusItem, WordFlashcard } from '../../api/vocabulary'
import { FlashCard } from '../../components/vocabulary/FlashCard'

interface ProgressData {
  total_words: number
  stats: { known?: number; familiar?: number; unknown?: number; pass?: number }
}

type StatusKey = 'known' | 'familiar' | 'pass' | 'unknown'

const STATUS_META: Record<StatusKey, { label: string; color: string; bg: string; border: string; icon: React.ReactNode }> = {
  known:    { label: 'Known',    color: 'var(--sat-green)',        bg: 'var(--sat-surface-success)', border: 'var(--sat-green-border-soft)', icon: <CheckCircle size={20} /> },
  familiar: { label: 'Familiar', color: 'var(--sat-warning-text)', bg: 'var(--sat-surface-warning)', border: 'var(--sat-yellow-soft)', icon: <HelpCircle size={20} /> },
  pass:     { label: 'Pass',     color: 'var(--sat-info-text)',    bg: 'var(--sat-surface-info)', border: 'var(--sat-surface-info)', icon: <Award size={20} /> },
  unknown:  { label: 'Unknown',  color: 'var(--sat-red)',          bg: 'var(--sat-surface-danger)', border: 'var(--sat-red-soft-strong)', icon: <XCircle size={20} /> },
}

// Height of one word row: word line + meaning line + vertical padding + divider
const ITEM_HEIGHT = 62
// Drawer chrome: header bar + drawer vertical padding + pagination row + pagination margin
const DRAWER_OVERHEAD = 160

function useItemsPerPage() {
  const [itemsPerPage, setItemsPerPage] = useState(() =>
    Math.max(1, Math.floor((window.innerHeight - DRAWER_OVERHEAD) / ITEM_HEIGHT))
  )

  useEffect(() => {
    const update = () =>
      setItemsPerPage(Math.max(1, Math.floor((window.innerHeight - DRAWER_OVERHEAD) / ITEM_HEIGHT)))
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  return itemsPerPage
}

export function VocabProgress() {
  const [data, setData] = useState<ProgressData | null>(null)
  const [loading, setLoading] = useState(true)
  const [drawerStatus, setDrawerStatus] = useState<StatusKey | null>(null)
  const [words, setWords] = useState<WordStatusItem[]>([])
  const [wordsLoading, setWordsLoading] = useState(false)
  const [page, setPage] = useState(1)
  const itemsPerPage = useItemsPerPage()
  const [flashcard, setFlashcard] = useState<WordFlashcard | null>(null)
  const [flashcardLoading, setFlashcardLoading] = useState(false)

  useEffect(() => {
    vocabularyApi.getProgress()
      .then((res) => { setData(res.data); setLoading(false) })
      .catch(() => setLoading(false))
  }, [])

  const openDrawer = (status: StatusKey) => {
    setDrawerStatus(status)
    setPage(1)
    setWords([])
    setWordsLoading(true)
    vocabularyApi.getWordsByStatus(status)
      .then((res) => setWords(res.data))
      .finally(() => setWordsLoading(false))
  }

  if (loading) {
    return (
      <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Loader color="eveningSea" />
      </Box>
    )
  }

  if (!data) {
    return (
      <Box style={{ textAlign: 'center', padding: 40 }}>
        <Text size="lg" c="var(--sat-red)">Failed to load progress data.</Text>
      </Box>
    )
  }

  const known = data.stats?.known ?? 0
  const familiar = data.stats?.familiar ?? 0
  const pass = data.stats?.pass ?? 0
  const unknown = data.stats?.unknown ?? 0
  const unseen = data.total_words - known - familiar - pass - unknown
  const knownPct = data.total_words > 0 ? Math.round((known / data.total_words) * 100) : 0

  const openFlashcard = (wordId: number) => {
    setFlashcard(null)
    setFlashcardLoading(true)
    vocabularyApi.getFlashcard(wordId)
      .then((res) => setFlashcard(res.data))
      .finally(() => setFlashcardLoading(false))
  }

  const meta = drawerStatus ? STATUS_META[drawerStatus] : null
  const totalPages = Math.max(1, Math.ceil(words.length / itemsPerPage))
  const pageWords = words.slice((page - 1) * itemsPerPage, page * itemsPerPage)

  return (
    <Container size="sm" py={32}>
      <Text size="xl" fw={700} c="var(--sat-text)" mb={24}>Vocabulary Progress</Text>

      <Paper shadow="sm" radius="md" p="xl" mb={24} style={{ background: 'var(--sat-surface)' }}>
        <Stack gap={4} align="center">
          <Text size="xs" c="var(--sat-text-soft)">Mastery</Text>
          <Text fw={800} c="var(--sat-green)" style={{ fontSize: 48 }}>{knownPct}%</Text>
          <Text size="sm" c="var(--sat-text-soft)">{known} of {data.total_words} words mastered</Text>
        </Stack>
      </Paper>

      {/* Non-clickable summary cards */}
      <Group grow gap={12} mb={16}>
        <Paper shadow="xs" radius="md" p="md" style={{ textAlign: 'center' }}>
          <Text fw={700} size="xl" c="var(--sat-text)">{data.total_words}</Text>
          <Text size="xs" c="var(--sat-text-soft)">Total Words</Text>
        </Paper>
        <Paper shadow="xs" radius="md" p="md" style={{ textAlign: 'center' }}>
          <Text fw={700} size="xl" c="var(--sat-blush)">{unseen}</Text>
          <Text size="xs" c="var(--sat-text-soft)">Unseen</Text>
        </Paper>
      </Group>

      {/* Clickable status cards */}
      <Group grow gap={12}>
        {(['known', 'familiar', 'pass', 'unknown'] as StatusKey[]).map((status) => {
          const m = STATUS_META[status]
          const count = data.stats?.[status] ?? 0
          return (
            <UnstyledButton key={status} onClick={() => openDrawer(status)} style={{ display: 'block' }}>
              <Paper
                shadow="xs"
                radius="md"
                p="md"
                style={{
                  textAlign: 'center',
                  background: m.bg,
                  border: `1.5px solid ${m.border}`,
                  cursor: 'pointer',
                  transition: 'transform 0.1s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-2px)')}
                onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
              >
                <Group justify="center" mb={4} style={{ color: m.color }}>{m.icon}</Group>
                <Text fw={700} size="xl" style={{ color: m.color }}>{count}</Text>
                <Text size="xs" c="var(--sat-text-soft)">{m.label}</Text>
              </Paper>
            </UnstyledButton>
          )
        })}
      </Group>

      {/* Word list drawer — no scroll, pagination only */}
      <Drawer
        opened={drawerStatus !== null}
        onClose={() => setDrawerStatus(null)}
        position="right"
        size="md"
        padding="md"
        title={
          meta && (
            <Group gap={8}>
              <span style={{ color: meta.color }}>{meta.icon}</span>
              <Text fw={700} c={meta.color}>{meta.label} Words</Text>
              <Badge color="gray" variant="light">{words.length}</Badge>
            </Group>
          )
        }
      >
        {wordsLoading ? (
          <Box style={{ display: 'flex', justifyContent: 'center', paddingTop: 40 }}>
            <Loader color="eveningSea" />
          </Box>
        ) : words.length === 0 ? (
          <Stack align="center" gap={8} pt={40}>
            <BookOpen size={32} color="var(--sat-icon-muted)" />
            <Text c="dimmed" size="sm">No words in this category yet.</Text>
          </Stack>
        ) : (
          <Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box style={{ flex: 1 }}>
              {pageWords.map((w, i) => (
                <Box key={w.id}>
                  {i > 0 && <Divider />}
                  <Group py={12} px={4} justify="space-between" wrap="nowrap">
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Text fw={600} size="sm">{w.word}</Text>
                      <Text size="xs" c="dimmed" mt={2} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{w.meaning}</Text>
                    </Box>
                    <ActionIcon
                      variant="subtle"
                      color="eveningSea"
                      size="sm"
                      onClick={() => openFlashcard(w.id)}
                      title="View flashcard"
                    >
                      <ExternalLink size={14} />
                    </ActionIcon>
                  </Group>
                </Box>
              ))}
            </Box>

            {totalPages > 1 && (
              <Group justify="center" pt={12}>
                <Pagination
                  value={page}
                  onChange={(p) => { setPage(p) }}
                  total={totalPages}
                  size="sm"
                  color="eveningSea"
                />
              </Group>
            )}
          </Box>
        )}
      </Drawer>
      {/* Flashcard modal */}
      <Modal
        opened={flashcard !== null || flashcardLoading}
        onClose={() => { setFlashcard(null); setFlashcardLoading(false) }}
        centered
        withCloseButton={false}
        size="auto"
        padding={0}
        overlayProps={{ backgroundOpacity: 0.75, blur: 4 }}
        styles={{ body: { padding: 0 }, content: { overflow: 'visible' } }}
      >
        {flashcardLoading ? (
          <Box style={{ width: 350, height: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--sat-surface)', borderRadius: 12 }}>
            <Loader color="eveningSea" />
          </Box>
        ) : flashcard ? (
          <FlashCard
            word={flashcard}
            onSwipeRight={() => { setFlashcard(null) }}
            onSwipeLeft={() => { setFlashcard(null) }}
            onSwipeUp={() => { setFlashcard(null) }}
            onSwipeDown={() => { setFlashcard(null) }}
            showBackFirst={false}
          />
        ) : null}
      </Modal>
    </Container>
  )
}
